import { normalizeProductCategoryAssignments } from "./productCategoryAssignments";

const STORAGE_KEY = "ls-discovery-v1";
const STATE_VERSION = 1;
const SIGNAL_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const SIGNAL_HALF_LIFE_MS = 30 * 24 * 60 * 60 * 1000;
const SIGNAL_COOLDOWN_MS = 30 * 60 * 1000;
const MAX_SIGNALS = 80;
const MAX_TRACKED_IDS = 500;

const emptyState = () => ({
  version: STATE_VERSION,
  signals: [],
  cycleSeenIds: [],
  lastBatchIds: [],
  lastRunId: null,
  lastRunSelectionIds: [],
});

const browserStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const cleanText = (value, maxLength = 120) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

export const productId = (product) =>
  cleanText(product?.id || product?._id || product?.slug, 160);

const normalizeCategoryKey = (value) =>
  cleanText(value, 100)
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const productCategoryKeys = (productOrCategory) => {
  if (typeof productOrCategory === "string") {
    const key = normalizeCategoryKey(productOrCategory);
    return key ? [key] : [];
  }

  const assignments = normalizeProductCategoryAssignments(productOrCategory || {});
  const candidates = assignments.map(
    (assignment) => assignment.category_slug || assignment.category || assignment.category_id,
  );
  if (!candidates.length) {
    candidates.push(productOrCategory?.category_slug || productOrCategory?.category);
  }
  return [...new Set(candidates.map(normalizeCategoryKey).filter(Boolean))];
};

export const productCategoryKey = (productOrCategory) =>
  productCategoryKeys(productOrCategory)[0] || "";

const uniqueCleanIds = (values, validIds = null) => {
  const result = [];
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const id = cleanText(value, 160);
    if (!id || seen.has(id) || (validIds && !validIds.has(id))) return;
    seen.add(id);
    result.push(id);
  });
  return result.slice(-MAX_TRACKED_IDS);
};

const normalizeState = (candidate, now = Date.now(), validIds = null) => {
  if (!candidate || candidate.version !== STATE_VERSION) return emptyState();

  const signals = (Array.isArray(candidate.signals) ? candidate.signals : [])
    .map((signal) => ({
      kind: signal?.kind === "category" ? "category" : "product",
      productId: cleanText(signal?.productId, 160),
      category: productCategoryKey(signal?.category),
      strength: Math.min(10, Math.max(0.25, Number(signal?.strength) || 1)),
      at: Number(signal?.at) || 0,
    }))
    .filter(
      (signal) =>
        signal.category &&
        signal.at > 0 &&
        signal.at <= now + 60 * 1000 &&
        now - signal.at <= SIGNAL_TTL_MS,
    )
    .slice(-MAX_SIGNALS);

  return {
    version: STATE_VERSION,
    signals,
    cycleSeenIds: uniqueCleanIds(candidate.cycleSeenIds, validIds),
    lastBatchIds: uniqueCleanIds(candidate.lastBatchIds, validIds),
    lastRunId: cleanText(candidate.lastRunId, 160) || null,
    lastRunSelectionIds: uniqueCleanIds(candidate.lastRunSelectionIds, validIds),
  };
};

export function readDiscoveryState(storage = browserStorage(), now = Date.now()) {
  if (!storage) return emptyState();
  try {
    const raw = storage.getItem(STORAGE_KEY);
    return raw ? normalizeState(JSON.parse(raw), now) : emptyState();
  } catch {
    return emptyState();
  }
}

export function writeDiscoveryState(state, storage = browserStorage(), now = Date.now()) {
  if (!storage) return false;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(normalizeState(state, now)));
    return true;
  } catch {
    return false;
  }
}

const saveSignal = (signal, { storage = browserStorage(), now = Date.now() } = {}) => {
  const category = productCategoryKey(signal.category);
  if (!category) return;
  const state = readDiscoveryState(storage, now);
  const product = cleanText(signal.productId, 160);
  const kind = product ? "product" : "category";
  const recentIndex = [...state.signals]
    .reverse()
    .findIndex(
      (item) =>
        item.kind === kind &&
        item.category === category &&
        item.productId === product &&
        now - item.at < SIGNAL_COOLDOWN_MS,
    );

  if (recentIndex >= 0) {
    const index = state.signals.length - 1 - recentIndex;
    state.signals[index] = {
      ...state.signals[index],
      at: now,
      strength: Math.max(state.signals[index].strength, signal.strength || 1),
    };
  } else {
    state.signals.push({
      kind,
      productId: product,
      category,
      strength: Math.min(10, Math.max(0.25, Number(signal.strength) || 1)),
      at: now,
    });
  }
  state.signals = state.signals.slice(-MAX_SIGNALS);
  writeDiscoveryState(state, storage, now);
};

export function recordProductInterest(product, options = {}) {
  const categories = productCategoryKeys(product);
  const id = productId(product);
  if (!categories.length || !id) return;
  categories.forEach((category, index) => {
    saveSignal(
      {
        productId: id,
        category,
        // Secondary catalogue placements contribute to discovery without
        // outweighing the deliberately selected primary placement.
        strength: (options.strength || 2) * (index === 0 ? 1 : 0.8),
      },
      options,
    );
  });
}

export function recordCategoryInterest(category, options = {}) {
  const categoryKey = productCategoryKey(category);
  if (!categoryKey || categoryKey === "bestsellers") return;
  saveSignal(
    {
      category: categoryKey,
      strength: options.strength || 1,
    },
    options,
  );
}

const buildAffinity = (state, catalog, wishlistIds, cartItems, now) => {
  const affinity = {};
  state.signals.forEach((signal) => {
    const age = Math.max(0, now - signal.at);
    const decay = Math.pow(0.5, age / SIGNAL_HALF_LIFE_MS);
    affinity[signal.category] = (affinity[signal.category] || 0) + signal.strength * decay;
  });

  const byId = new Map(catalog.map((product) => [productId(product), product]));
  uniqueCleanIds(wishlistIds).forEach((id) => {
    productCategoryKeys(byId.get(id)).forEach((category) => {
      affinity[category] = (affinity[category] || 0) + 5;
    });
  });
  (Array.isArray(cartItems) ? cartItems : []).forEach((item) => {
    const source = byId.get(cleanText(item?.product_id, 160)) || item?.category;
    productCategoryKeys(source).forEach((category) => {
      affinity[category] = (affinity[category] || 0) + 7;
    });
  });
  return affinity;
};

const strongestProductCategory = (product, affinity) => {
  const categories = productCategoryKeys(product);
  return categories.reduce(
    (best, category) => ((affinity[category] || 0) > (affinity[best] || 0) ? category : best),
    categories[0] || "",
  );
};

const weightedOrder = (products, affinity, rng, selectedCategoryCounts) => {
  const remaining = [...products];
  const ordered = [];
  while (remaining.length) {
    const weights = remaining.map((product) => {
      const category = strongestProductCategory(product, affinity);
      const preference = Math.min(18, affinity[category] || 0);
      const editorialBoost =
        (product.is_bestseller ? 1.2 : 0) +
        (product.is_top_rated ? 0.8 : 0) +
        (product.is_new_arrival ? 0.45 : 0);
      const diversityPenalty = 1 + (selectedCategoryCounts[category] || 0) * 0.42;
      return Math.max(0.1, (1 + preference * 0.72 + editorialBoost) / diversityPenalty);
    });
    const total = weights.reduce((sum, value) => sum + value, 0);
    const randomValue = Math.min(0.999999999, Math.max(0, Number(rng()) || 0));
    let target = randomValue * total;
    let pickedIndex = weights.length - 1;
    for (let index = 0; index < weights.length; index += 1) {
      target -= weights[index];
      if (target <= 0) {
        pickedIndex = index;
        break;
      }
    }
    const [picked] = remaining.splice(pickedIndex, 1);
    ordered.push(picked);
    const category = strongestProductCategory(picked, affinity);
    selectedCategoryCounts[category] = (selectedCategoryCounts[category] || 0) + 1;
  }
  return ordered;
};

export function selectDiscoveryProducts(
  products,
  {
    limit = 12,
    visibleLimit = limit,
    state = emptyState(),
    wishlistIds = [],
    cartItems = [],
    rng = Math.random,
    now = Date.now(),
    runId = null,
  } = {},
) {
  const catalog = [];
  const validIds = new Set();
  (Array.isArray(products) ? products : []).forEach((product) => {
    const id = productId(product);
    if (!id || product?.is_active === false || validIds.has(id)) return;
    validIds.add(id);
    catalog.push(product);
  });

  const safeState = normalizeState(state, now, validIds);
  const wanted = Math.max(0, Math.min(Number(limit) || 0, catalog.length));
  const committedCount = Math.max(0, Math.min(Number(visibleLimit) || wanted, wanted));
  const byId = new Map(catalog.map((product) => [productId(product), product]));
  const affinity = buildAffinity(safeState, catalog, wishlistIds, cartItems, now);

  if (
    runId &&
    safeState.lastRunId === runId &&
    safeState.lastRunSelectionIds.length >= wanted
  ) {
    const replay = safeState.lastRunSelectionIds
      .map((id) => byId.get(id))
      .filter(Boolean)
      .slice(0, wanted);
    if (replay.length === wanted) {
      return { items: replay, nextState: safeState, meta: discoveryMeta(affinity, catalog) };
    }
  }

  const lastBatch = new Set(safeState.lastBatchIds);
  const cycleSeen = new Set(safeState.cycleSeenIds);
  const tiers = [[], [], [], []];
  catalog.forEach((product) => {
    const id = productId(product);
    const wasLast = lastBatch.has(id);
    const wasSeen = cycleSeen.has(id);
    const tier = !wasLast && !wasSeen ? 0 : !wasLast && wasSeen ? 1 : wasLast && !wasSeen ? 2 : 3;
    tiers[tier].push(product);
  });

  const selectedCategoryCounts = {};
  const items = [];
  for (const tier of tiers) {
    if (items.length >= wanted) break;
    const ordered = weightedOrder(tier, affinity, rng, selectedCategoryCounts);
    items.push(...ordered.slice(0, wanted - items.length));
  }

  const visibleIds = items.slice(0, committedCount).map(productId);
  visibleIds.forEach((id) => cycleSeen.add(id));
  const cycleComplete = catalog.length > 0 && cycleSeen.size >= catalog.length;
  const nextState = {
    ...safeState,
    cycleSeenIds: cycleComplete ? visibleIds : [...cycleSeen].slice(-MAX_TRACKED_IDS),
    lastBatchIds: visibleIds,
    lastRunId: cleanText(runId, 160) || null,
    lastRunSelectionIds: items.map(productId),
  };

  return { items, nextState, meta: discoveryMeta(affinity, catalog) };
}

const discoveryMeta = (affinity, catalog) => {
  const preferredEntry = Object.entries(affinity).sort((a, b) => b[1] - a[1])[0];
  const preferredKey = preferredEntry?.[1] > 0 ? preferredEntry[0] : null;
  const preferredProduct = preferredKey
    ? catalog.find((product) => productCategoryKeys(product).includes(preferredKey))
    : null;
  return {
    personalized: Boolean(preferredKey),
    preferredCategory: preferredProduct?.category || null,
  };
};

export function getDiscoveryRecommendations(products, options = {}) {
  const storage = options.storage === undefined ? browserStorage() : options.storage;
  const now = options.now || Date.now();
  const state = readDiscoveryState(storage, now);
  const result = selectDiscoveryProducts(products, { ...options, storage: undefined, state, now });
  writeDiscoveryState(result.nextState, storage, now);
  return result;
}

export function createDiscoveryRunId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function clearDiscoveryState(storage = browserStorage()) {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {}
}

export { STORAGE_KEY as DISCOVERY_STORAGE_KEY };
